import { useState } from 'react';
import {
  Stack,
  Text,
  TextInput,
  Button,
  Group,
  ActionIcon,
  Card,
  Table,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ProductAttribute } from '@/types';

interface AttributesEditorProps {
  attributes: Record<string, ProductAttribute>;
  onChange: (attributes: Record<string, ProductAttribute>) => void;
}

export function AttributesEditor({ attributes, onChange }: AttributesEditorProps) {
  const [localAttributes, setLocalAttributes] = useState<Array<{ key: string; attr: ProductAttribute }>>(
    Object.entries(attributes || {}).map(([key, attr]) => ({ key, attr }))
  );

  const updateAttributes = (newAttributes: Array<{ key: string; attr: ProductAttribute }>) => {
    setLocalAttributes(newAttributes);
    const attributesObj: Record<string, ProductAttribute> = {};
    newAttributes.forEach(({ key, attr }) => {
      if (key.trim() && attr.label.trim()) {
        attributesObj[key.trim()] = attr;
      }
    });
    onChange(attributesObj);
  };

  const addAttribute = () => {
    updateAttributes([...localAttributes, { key: '', attr: { label: '', value: '' } }]);
  };

  const removeAttribute = (index: number) => {
    updateAttributes(localAttributes.filter((_, i) => i !== index));
  };

  const updateAttribute = (index: number, field: 'key' | 'label' | 'value' | 'unit', value: string) => {
    const newAttributes = [...localAttributes];
    if (field === 'key') {
      newAttributes[index].key = value;
    } else {
      newAttributes[index].attr = { ...newAttributes[index].attr, [field]: value };
    }
    updateAttributes(newAttributes);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Text size="sm" fw={500} mb="xs">
            Product Attributes (Specifications)
          </Text>
          <Text size="xs" c="dimmed">
            Add specifications that will be displayed in the specification table
          </Text>
        </div>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          onClick={addAttribute}
          size="sm"
        >
          Add Attribute
        </Button>
      </Group>

      {localAttributes.length > 0 ? (
        <Card withBorder padding="md" radius="md">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: '20%' }}>Key</Table.Th>
                <Table.Th style={{ width: '30%' }}>Label</Table.Th>
                <Table.Th style={{ width: '30%' }}>Value</Table.Th>
                <Table.Th style={{ width: '15%' }}>Unit</Table.Th>
                <Table.Th style={{ width: '5%' }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {localAttributes.map((item, index) => (
                <Table.Tr key={index}>
                  <Table.Td>
                    <TextInput
                      placeholder="model"
                      value={item.key}
                      onChange={(e) => updateAttribute(index, 'key', e.target.value)}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      placeholder="Model"
                      value={item.attr.label}
                      onChange={(e) => updateAttribute(index, 'label', e.target.value)}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      placeholder="SL-C02-A"
                      value={item.attr.value}
                      onChange={(e) => updateAttribute(index, 'value', e.target.value)}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      placeholder="W, V, mm, g..."
                      value={item.attr.unit || ''}
                      onChange={(e) => updateAttribute(index, 'unit', e.target.value)}
                      size="xs"
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() => removeAttribute(index)}
                      size="sm"
                    >
                      <IconTrash size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      ) : (
        <Card withBorder padding="xl" radius="md" style={{ textAlign: 'center' }}>
          <Text c="dimmed">No attributes added yet</Text>
          <Button
            leftSection={<IconPlus size={16} />}
            variant="light"
            onClick={addAttribute}
            size="sm"
            mt="md"
          >
            Add First Attribute
          </Button>
        </Card>
      )}
    </Stack>
  );
}

